import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../lib/utils';
import type { AvailabilityState, PeriodState } from '../api/unavailabilitiesApi';
import { availabilityLabel, periodStateLabel } from '../utils/unavailability';

/**
 * The two pills an absence is read through, and the four tints behind them.
 *
 * One component rather than the same eight lines on the mobile screen, in the record card and in
 * the directory column: three copies of a pill are three chances for one of them to say
 * "Indisponible" in a different amber. The wording stays in `utils/unavailability.ts` — words are
 * not markup — and the tints live here, with the only thing that renders them.
 *
 * Rose for an absence in progress, amber for one to come, emerald for available, zinc for what is
 * over — the same mapping on both sides of the app.
 */

/**
 * Exported so the one pill that carries no state — the neutral "Ma disponibilité" the ticket
 * screen shows when the read failed — has the same shape without inventing a fourth
 * `AvailabilityState` that the API would never send.
 */
export const PILL = 'inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium';

const AVAILABILITY_TONE: Record<AvailabilityState, string> = {
  available: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400',
  unavailable: 'bg-rose-50 text-rose-700 dark:bg-rose-950 dark:text-rose-400',
  upcoming: 'bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-400',
};

const AVAILABILITY_DOT: Record<AvailabilityState, string> = {
  available: 'bg-emerald-500',
  unavailable: 'bg-rose-500',
  upcoming: 'bg-amber-500',
};

const PERIOD_TONE: Record<PeriodState, string> = {
  current: AVAILABILITY_TONE.unavailable,
  upcoming: AVAILABILITY_TONE.upcoming,
  past: 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300',
};

/** The standalone dot the two period lists draw *before* the range, outside the badge. */
export const PERIOD_DOT: Record<PeriodState, string> = {
  current: AVAILABILITY_DOT.unavailable,
  upcoming: AVAILABILITY_DOT.upcoming,
  past: 'bg-zinc-400',
};

/**
 * A technician's derived state: a dot and a word.
 *
 * `children` is rendered after the word, inside the tint — the ticket screen's pill puts its
 * chevron there. A slot rather than a `variant` prop: the chevron is the only thing any caller
 * has ever wanted to add, and a slot costs one line where a variant costs a mapping.
 */
export const AvailabilityBadge = ({
  state,
  className,
  children,
}: {
  state: AvailabilityState;
  className?: string;
  children?: ReactNode;
}) => {
  const { t } = useTranslation();

  return (
    <span className={cn(PILL, AVAILABILITY_TONE[state], className)}>
      <span className={cn('h-1.5 w-1.5 rounded-full', AVAILABILITY_DOT[state])} aria-hidden="true" />
      {availabilityLabel(t, state)}
      {children}
    </span>
  );
};

/** One period's own state. No dot inside: the lists draw theirs beside the range. */
export const PeriodBadge = ({ state, className }: { state: PeriodState; className?: string }) => {
  const { t } = useTranslation();

  return <span className={cn(PILL, PERIOD_TONE[state], className)}>{periodStateLabel(t, state)}</span>;
};
